import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogTitle,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { getProductGroup, getCategories, getSubCategories } from "../../../api/Product/api";
import { X } from "lucide-react";

const ProductForm = ({ initialData, onSubmit, onCancel }) => {
  const [productGroup, setProductGroup] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const defaultData = {
    code: "",
    sku: "",
    name: "",
    price: "",
    product_group: "",
    category: "",
    sub_category: "",
    classification: "",
    strategy: "",
    lead_time: "",
    coverage_profile: "",
    planning_cycle: "",
    mrp_collector: "",
    rounding_value: "",
    min_order_quantity: "",
    max_order_quantity: "",
  }
  const [formData, setFormData] = useState(
    initialData || defaultData
  );
  const [errors, setErrors] = useState({ sku: "", name: "" });

  useEffect(() => {
    setFormData(initialData || defaultData);
  }, [initialData]);

  const fetchProductGroup = async () => {
    try {
      const response = await getProductGroup();
      setProductGroup(response.data);
    } catch (error) {
      console.error("Error fetching product group:", error);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data.results || data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const data = await getSubCategories();
      setSubCategories(data.results || data);
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  };

  const validateForm = () => {
    let newErrors = { sku: "", name: "" };
    let isValid = true;

    // SKU Validation: Only letters, numbers, hyphens (-), and underscores (_)
    if (!/^[A-Za-z0-9_-]+$/.test(formData.sku)) {
      newErrors.sku =
        "SKU can only contain letters, numbers, hyphens (-), and underscores (_).";
      isValid = false;
    }

    // Product Name Validation: Must start with a letter, can contain letters, numbers, and spaces
    if (!/^[A-Za-z][A-Za-z0-9\s]*$/.test(formData.name)) {
      newErrors.name =
        "Product name must start with a letter and can contain letters, numbers, and spaces.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  
  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchProductGroup();
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          pb: 3,
          fontSize: 18,
          fontWeight: 500,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {initialData ? "Edit Product" : "Add New Product"}
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={5}>
        <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Code"
              name="code"
              value={formData.code}
              onChange={(e) =>
                // setFormData({ ...formData, code: e.target.value })
                setFormData(prev => ({ ...prev, code: e.target.value }))
              }
              required
              size="medium"
              error={!!errors.code}
              helperText={errors.code}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={(e) =>
                setFormData({ ...formData, sku: e.target.value })
              }
              required
              size="medium"
              error={!!errors.sku}
              helperText={errors.sku}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              size="medium"
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Price (KES)"
              type="number"
              value={formData.price}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  price: parseFloat(e.target.value),
                })
              }
              required
              size="medium"
              inputProps={{ min: 0, step: "0.01" }}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel id="product-group-label">Product Group</InputLabel>
              <Select
                labelId="product-group-label"
                name="product_group"
                value={formData.product_group}
                label="Product Group"
                onChange={handleChange}
              >
                {productGroup.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                name="category"
                value={formData.category}
                label="Category"
                onChange={handleChange}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel id="sub-category-label">Sub-Category</InputLabel>
              <Select
                labelId="sub-category-label"
                name="sub_category"
                value={formData.sub_category}
                label="Sub-Category"
                onChange={handleChange}
              >
                {subCategories.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Classification"
              name="classification"
              value={formData.classification}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Strategy"
              name="strategy"
              required
              value={formData.strategy}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Lead Time (Days)"
              name="lead_time"
              type="number"
              required
              value={formData.lead_time}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Coverage Profile (Days)"
              name="coverage_profile"
              type="number"
              required
              value={formData.coverage_profile}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Planning Cycle"
              name="planning_cycle"
              required
              value={formData.planning_cycle}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="MRP Collector"
              name="mrp_collector"
              type="number"
              required
              value={formData.mrp_collector}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Rounding Value"
              name="rounding_value"
              type="number"
              required
              value={formData.rounding_value}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Minimum Order Quantity"
              name="min_order_quantity"
              type="number"
              required
              value={formData.min_order_quantity}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Maximum Order Quantity"
              name="max_order_quantity"
              type="number"
              value={formData.max_order_quantity}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: "#e0e0e0",
                  color: "#000",
                  "&:hover": { bgcolor: "#d5d5d5" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: "#00694B",
                  color: "#fff",
                  "&:hover": { bgcolor: "#005039" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                {initialData ? "Update" : "Create"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ProductForm;
